import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatTableModule } from '@angular/material/table'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { catchError, of, tap } from 'rxjs'

import { LoadingComponent } from '../../../../../components/loading/loading.component'
import {
  DialogResult,
  MessageDialogService,
} from '../../../../../components/message-dialog/message-dialog.service'
import {
  BiztelAccountMappingService,
  BiztelAccountMappingView,
  ImportBiztelAccountMappingResultView,
} from '../../../../../services/external-integration/biztel-integration/biztel-account-mapping.service'
import { captureException } from '../../../../../util/captureException'

@UntilDestroy()
@Component({
  selector: 'app-biztel-account-mapping',
  standalone: true,
  imports: [MatTableModule, LoadingComponent],
  templateUrl: './biztel-account-mapping.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './biztel-account-mapping.component.scss',
})
export class BiztelAccountMappingComponent implements OnInit {
  mappings: BiztelAccountMappingView[] = []
  importResult: ImportBiztelAccountMappingResultView | null = null
  isLoading = false
  isImporting = false
  readonly mappingColumns = ['biztelAccountId', 'name', 'email']
  readonly errorColumns = ['row', 'message']

  constructor(
    private biztelAccountMappingService: BiztelAccountMappingService,
    private snackBar: MatSnackBar,
    private messageDialogService: MessageDialogService,
  ) {}

  ngOnInit(): void {
    this.loadMappings()
  }

  // BIZTEL の取り込みはフォーマット不正・重複を全件拒否する all-or-nothing 方式。
  // errors が 1 件でもあれば imported / deletedMappingCount は 0 で既存マッピングは
  // 不変なので、bluebean の「スキップ件数」表示とは UI を分岐させる。
  get isRejected(): boolean {
    return (this.importResult?.errors.length ?? 0) > 0
  }

  // backend の row は「header=1 / データ行=2..」。ファイル全体に対するエラー
  // (EMPTY_FILE / 行数超過=0、DUPLICATE_USER_RESOLUTION=-1、ヘッダ欠落=1) は特定の
  // データ行を指さないため「—」に潰し、データ行 (>=2) のみ番号を出す。
  formatErrorRow(row: number): string {
    return row >= 2 ? String(row) : '—'
  }

  // 取り込みは既存マッピングの全置換（破壊的操作）なので、まず dry-run で
  // 「取り込むと何が起きるか」の件数を取り、確認ダイアログで管理者が確定した
  // ときだけ実行する 2 段階フローにする。
  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement
    const file = target.files?.[0]
    if (!file) {
      return
    }
    this.readAndPreview(file, target)
  }

  private readAndPreview(file: File, target: HTMLInputElement): void {
    this.isImporting = true
    this.importResult = null

    const reader = new FileReader()
    reader.onerror = () => {
      this.notifyFileReadFailure(target)
    }
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        this.notifyFileReadFailure(target)
        return
      }
      // result は `data:*;base64,XXX` の形式。base64 本体だけを抽出する。
      const base64Body = result.split(',', 2)[1]
      if (base64Body == null) {
        this.notifyFileReadFailure(target)
        return
      }
      this.previewImport(base64Body, target)
    }
    reader.readAsDataURL(file)
  }

  private notifyFileReadFailure(target: HTMLInputElement): void {
    this.isImporting = false
    this.snackBar.open('ファイルの読み込みに失敗しました。', '閉じる', {
      duration: 6000,
    })
    target.value = ''
  }

  private previewImport(csvBase64: string, fileInput: HTMLInputElement): void {
    this.biztelAccountMappingService
      .importBiztelAccountMapping(csvBase64, true)
      .pipe(
        tap((result) => {
          this.isImporting = false
          if (!result) {
            // throwIfCustomError 経由でも catchError 経由でもない fallback パス。
            // 念のため snackBar でユーザーに通知する。
            this.snackBar.open('CSV のインポートに失敗しました。', '閉じる', {
              duration: 6000,
            })
            fileInput.value = ''
            return
          }
          if (result.errors.length > 0) {
            // 全件拒否。確認ダイアログには進まず、中止を明示してエラー内容
            // (importResult.errors) をテンプレートで表示する。
            this.importResult = result
            this.snackBar.open(
              'CSV にエラーがあるため取り込みを中止しました。',
              '閉じる',
              { duration: 6000 },
            )
            fileInput.value = ''
            return
          }
          // dry-run 結果は確認ダイアログの生成にだけ使い、importResult には
          // 入れない (確定前に「インポート結果」サマリが画面に出るのを防ぐ)。
          this.confirmAndExecute(result, csvBase64, fileInput)
        }),
        catchError((error: unknown) => {
          captureException(error)
          this.isImporting = false
          this.snackBar.open(
            'CSV のインポートに失敗しました。フォーマットを確認してください。',
            '閉じる',
            { duration: 6000 },
          )
          fileInput.value = ''
          return of(undefined)
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  private confirmAndExecute(
    preview: ImportBiztelAccountMappingResultView,
    csvBase64: string,
    fileInput: HTMLInputElement,
  ): void {
    // ダイアログ本文は [innerHTML] で描画されるため、埋め込むのは件数 (数値) のみ。
    // CSV 由来の文字列は埋め込まない。
    // 件数は「現在 → 実行後」の増減で見せる (意図しない大幅縮小に気付きやすくする)。
    // 全置換のため「現在」= 削除予定の既存全件数、「実行後」= 登録予定件数。
    const skippedTotal = preview.skippedNoEmail + preview.skippedUnregistered
    const message = [
      `実行すると、連携対象ユーザーは ${preview.deletedMappingCount} 人 → ${preview.imported} 人になります。`,
      '（現在の設定をすべて削除し、CSV の内容で置き換えます）',
      ...(skippedTotal > 0
        ? [
            '',
            'CSV のうち取り込まれない行:',
            `・メールアドレス未入力: ${preview.skippedNoEmail} 件`,
            `・AI Sales Coach に未登録: ${preview.skippedUnregistered} 件`,
          ]
        : []),
      '',
      '実行しますか？',
    ].join('\n')

    this.messageDialogService
      .showConfirm(message, {
        primaryButtonText: '実行',
        secondaryButtonText: 'キャンセル',
      })
      .pipe(untilDestroyed(this))
      .subscribe((dialogResult) => {
        if (dialogResult !== DialogResult.PrimaryButtonClicked) {
          // 中止時は同じファイルを選び直せるよう input をリセットする。
          fileInput.value = ''
          return
        }
        this.executeImport(csvBase64, fileInput)
      })
  }

  private executeImport(csvBase64: string, fileInput: HTMLInputElement): void {
    this.isImporting = true
    this.biztelAccountMappingService
      .importBiztelAccountMapping(csvBase64, false)
      .pipe(
        tap((result) => {
          if (!result) {
            this.snackBar.open('CSV のインポートに失敗しました。', '閉じる', {
              duration: 6000,
            })
            return
          }
          this.importResult = result
          if (result.errors.length > 0) {
            // dry-run 後に User や既存マッピングが変わった場合、実行時の再検証で
            // ここに来うる (dry-run は参考値)。全件拒否で一覧は変わっていない。
            this.snackBar.open(
              'CSV にエラーがあるため取り込みを中止しました。',
              '閉じる',
              { duration: 6000 },
            )
            return
          }
          this.snackBar.open('連携対象ユーザーを保存しました。', '閉じる')
          this.loadMappings()
        }),
        catchError((error: unknown) => {
          captureException(error)
          this.snackBar.open(
            'CSV のインポートに失敗しました。フォーマットを確認してください。',
            '閉じる',
            { duration: 6000 },
          )
          return of(undefined)
        }),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.isImporting = false
        fileInput.value = ''
      })
  }

  private loadMappings(): void {
    this.isLoading = true
    this.biztelAccountMappingService
      .getBiztelAccountMappings()
      .pipe(
        catchError((error: unknown) => {
          captureException(error)
          this.snackBar.open(
            '連携対象ユーザー一覧の取得に失敗しました。',
            '閉じる',
            {
              duration: 6000,
            },
          )
          return of([] as BiztelAccountMappingView[])
        }),
        untilDestroyed(this),
      )
      .subscribe((mappings) => {
        this.mappings = mappings
        this.isLoading = false
      })
  }
}
